import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation, useParams, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import axios from 'axios';
import { 
  House, Package, ShoppingCart, Users, Gear, ChartLine,
  FileText, CreditCard, Truck, SignOut, List,
  Eye, Ticket, Globe, Cube, Palette, Megaphone,
  Image as ImageIcon, ArrowLeft, Bell, Storefront,
  CaretDown, CaretUp, Star, Tag, Chat, CurrencyEur,
  ArrowsClockwise, EnvelopeSimple, MapTrifold, User, IdentificationBadge, FolderOpen, MagnifyingGlass, Buildings, Scales, Question
} from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { PwaInstallButton } from './PwaInstallButton';
import { AdminGuideTour, TOUR_STORAGE_KEY } from './AdminGuideTour';
import { useNotifications } from '../context/NotificationContext';
import useNewOrderSound from '../hooks/useNewOrderSound';

const AdminPushToggle = () => {
  const { pushEnabled, pushSupported, subscribePush } = useNotifications();
  if (!pushSupported || pushEnabled) return null;
  return (
    <Button
      variant="outline" size="sm"
      className="text-xs h-8 gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50"
      onClick={subscribePush}
      title="Recevoir les nouvelles commandes dans le centre de notifications de l'appareil"
      data-testid="admin-enable-push"
    >
      <Bell size={14} weight="fill" /> <span className="hidden sm:inline">Activer notifs</span>
    </Button>
  );
};

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_STORE_SLUG = "magere-viandes";

const StoreAdminLayout = ({ storeSlug: propStoreSlug, standalone = false }) => {
  const { storeSlug: paramStoreSlug } = useParams();
  const storeSlug = propStoreSlug || paramStoreSlug || DEFAULT_STORE_SLUG;
  const { user, logout } = useAuth();
  const { stores, selectStore, fetchStores, currentStore } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [storeIdentity, setStoreIdentity] = useState(null);
  const [quickStats, setQuickStats] = useState(null);
  const [openSections, setOpenSections] = useState({ catalogue: true, commerce: true, logistique: true, contenu: true, seo: false, config: false });
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(TOUR_STORAGE_KEY) !== '1') {
      const t = setTimeout(() => setTourOpen(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  // Poll for new paid orders and play a cash-register sound when they arrive
  useNewOrderSound(currentStore?.id, !!currentStore?.id);

  useEffect(() => { fetchStores(); }, [fetchStores]);

  useEffect(() => {
    if (stores.length > 0 && storeSlug) {
      const matchingStore = stores.find(s => s.slug === storeSlug);
      if (matchingStore) {
        if (!currentStore || currentStore.id !== matchingStore.id) selectStore(matchingStore);
      } else if (!standalone) {
        navigate('/dashboard');
      }
    }
  }, [stores, storeSlug, selectStore, currentStore, navigate, standalone]);

  useEffect(() => {
    if (!currentStore?.id) return;
    axios.get(`${API_URL}/api/stores/${currentStore.id}/identity`, { withCredentials: true })
      .then(res => setStoreIdentity(res.data)).catch(() => {});
    const loadStats = () => axios.get(`${API_URL}/api/stores/${currentStore.id}/stats?period=30d`, { withCredentials: true })
      .then(res => setQuickStats(res.data)).catch(() => {});
    loadStats();
    const it = setInterval(loadStats, 60000); // refresh every 60s
    return () => clearInterval(it);
  }, [currentStore?.id]);

  const handleLogout = async () => { await logout(); navigate(standalone ? '/admin/login' : '/login'); };

  const basePath = standalone ? '/admin' : `/store/${storeSlug}/admin`;
  const primaryColor = currentStore?.theme_config?.colors?.primary || storeIdentity?.primary_color || '#c2185b';
  const logoUrl = storeIdentity?.logo_light || storeIdentity?.logo_url || currentStore?.theme_config?.logo_url;
  const storeName = currentStore?.name || storeSlug;

  const sections = [
    {
      key: 'catalogue',
      label: 'Catalogue',
      accent: '#f59e0b', // amber
      items: [
        { icon: Package, label: 'Produits', path: `${basePath}/products` },
        { icon: FolderOpen, label: 'Catégories', path: `${basePath}/categories` },
        { icon: Cube, label: 'Stock', path: `${basePath}/stock` },
      ]
    },
    {
      key: 'commerce',
      label: 'Commerce',
      accent: '#10b981', // emerald
      items: [
        { icon: ShoppingCart, label: 'Commandes', path: `${basePath}/orders`, badge: quickStats?.total_orders },
        { icon: ChartLine, label: 'Statistiques', path: `${basePath}/stats` },
        { icon: CreditCard, label: 'Paiements', path: `${basePath}/payments` },
        { icon: Users, label: 'Clients', path: `${basePath}/customers` },
        { icon: MapTrifold, label: 'Carte clients', path: `${basePath}/customers-map` },
        { icon: Star, label: 'Fidélité', path: `${basePath}/loyalty` },
        { icon: Ticket, label: 'Marketing & Promos', path: `${basePath}/marketing` },
      ]
    },
    {
      key: 'logistique',
      label: 'Logistique',
      accent: '#8b5cf6', // violet
      items: [
        { icon: Truck, label: 'Règles de livraison', path: `${basePath}/shipping` },
        { icon: Truck, label: 'Livreurs', path: `${basePath}/drivers` },
        { icon: IdentificationBadge, label: 'Commerciaux', path: `${basePath}/commerciaux` },
      ]
    },
    {
      key: 'contenu',
      label: 'Contenu du site',
      accent: '#ec4899', // pink
      items: [
        { icon: Globe, label: 'Pages du site', path: `${basePath}/pages` },
        { icon: FileText, label: 'Blog / Conseils', path: `${basePath}/blog` },
        { icon: Megaphone, label: 'Popups & bannières', path: `${basePath}/popups` },
        { icon: ImageIcon, label: 'Médias', path: `${basePath}/media` },
      ]
    },
    {
      key: 'seo',
      label: 'SEO & Visibilité',
      accent: '#0ea5e9', // sky
      items: [
        { icon: MagnifyingGlass, label: 'Suivi SEO Google', path: `${basePath}/seo-rank` },
      ]
    },
    {
      key: 'config',
      label: 'Configuration',
      accent: '#64748b', // slate
      items: [
        { icon: Buildings, label: 'Entreprise', path: `${basePath}/company` },
        { icon: Palette, label: 'Identité visuelle', path: `${basePath}/identity` },
        { icon: EnvelopeSimple, label: 'Notifications & SMTP', path: `${basePath}/notifications` },
        { icon: CreditCard, label: 'Méthodes de paiement', path: `${basePath}/payment-methods` },
        { icon: Scales, label: 'Documents légaux', path: `${basePath}/legal` },
        { icon: User, label: 'Compte utilisateur', path: `${basePath}/account` },
        { icon: Gear, label: 'SEO & Tracking', path: `${basePath}/settings` },
      ]
    },
  ];

  const isActive = (path) => location.pathname === path;
  const toggleSection = (key) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#f8f7f5' }} data-testid="store-admin-layout">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          data-testid="mobile-backdrop"
        />
      )}
      {/* ═══════ SIDEBAR ═══════ */}
      <aside
        className={`
          ${sidebarOpen ? 'md:w-[260px]' : 'md:w-[72px]'}
          w-[280px]
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0
          transition-all duration-300 flex flex-col border-r border-slate-200/60 fixed left-0 top-0 bottom-0 z-50
          overflow-hidden
        `}
        style={{
          background: `linear-gradient(180deg, #1e293b 0%, #0f172a 100%)`,
          boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.03)`,
        }}
      >
        {/* Ambient glow — subtle brand accent */}
        <div
          className="pointer-events-none absolute -top-16 -left-16 w-64 h-64 rounded-full opacity-25 blur-3xl"
          style={{ backgroundColor: primaryColor }}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-24 -right-16 w-72 h-72 rounded-full opacity-10 blur-3xl"
          style={{ backgroundColor: '#3b82f6' }}
          aria-hidden
        />

        {/* Store branding */}
        <div className="relative px-4 py-5 border-b border-white/10">
          <Link to={basePath} className="flex items-center gap-3" data-testid="store-admin-logo">
            {logoUrl ? (
              <img src={logoUrl} alt={storeName} className="h-11 w-11 object-contain rounded-xl bg-white/10 p-0.5 ring-1 ring-white/10" />
            ) : (
              <div className="w-11 h-11 rounded-xl flex items-center justify-center ring-2 ring-white/10 shadow-lg" style={{ backgroundColor: primaryColor, boxShadow: `0 4px 16px ${primaryColor}55` }}>
                <span className="text-white font-black text-base">{storeName.charAt(0)}</span>
              </div>
            )}
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-white font-bold text-[15px] truncate leading-tight">{storeName}</p>
                <p className="text-[11px] font-medium mt-0.5 truncate flex items-center gap-1" style={{ color: primaryColor }}>
                  <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: primaryColor }} />
                  Back-office
                </p>
              </div>
            )}
          </Link>
        </div>

        {/* Quick actions */}
        {sidebarOpen && !standalone && (
          <div className="relative px-3 py-3 border-b border-white/10 flex gap-2">
            <Button 
              variant="ghost" size="sm"
              className="flex-1 text-[11px] h-8 text-white/70 hover:text-white hover:bg-white/10 rounded-lg"
              onClick={() => navigate('/dashboard')}
              data-testid="back-to-admin-btn"
            >
              <ArrowLeft size={13} className="mr-1" /> Admin
            </Button>
          </div>
        )}

        {/* Dashboard link */}
        <div className="relative px-3 pt-3 pb-1">
          <Link
            to={basePath}
            onClick={() => setMobileOpen(false)}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
              isActive(basePath) ? 'text-white shadow-lg' : 'text-white/65 hover:text-white hover:bg-white/[0.06] hover:translate-x-0.5'
            }`}
            style={isActive(basePath) ? { backgroundColor: primaryColor, boxShadow: `0 4px 16px ${primaryColor}55` } : {}}
            data-testid="store-nav-dashboard"
          >
            <House size={20} weight={isActive(basePath) ? 'fill' : 'regular'} className={isActive(basePath) ? '' : 'group-hover:scale-110 transition-transform'} />
            {sidebarOpen && <span className="text-[13px] font-semibold">Tableau de bord</span>}
          </Link>
        </div>

        {/* Navigation sections */}
        <nav className="relative flex-1 py-2 overflow-y-auto px-3">
          {sections.map(section => (
            <div key={section.key} className="mb-1">
              {sidebarOpen && (
                <button
                  onClick={() => toggleSection(section.key)}
                  className="flex items-center justify-between w-full px-3 py-2 text-[11px] font-bold uppercase tracking-[0.08em] hover:opacity-100 transition-opacity"
                  style={{ color: section.accent, opacity: 0.75 }}
                >
                  <span className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full shadow-sm" style={{ backgroundColor: section.accent, boxShadow: `0 0 8px ${section.accent}` }} />
                    {section.label}
                  </span>
                  {openSections[section.key] ? <CaretUp size={12} /> : <CaretDown size={12} />}
                </button>
              )}
              {(openSections[section.key] || !sidebarOpen) && (
                <ul className="space-y-0.5">
                  {section.items.map(item => {
                    const active = isActive(item.path);
                    return (
                      <li key={item.path}>
                        <Link
                          to={item.path}
                          onClick={() => setMobileOpen(false)}
                          className={`relative flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-200 group ${
                            active
                              ? 'text-white'
                              : 'text-white/65 hover:text-white hover:bg-white/[0.06] hover:translate-x-0.5'
                          }`}
                          style={active ? {
                            backgroundColor: (section.accent || primaryColor) + '20',
                            color: '#ffffff',
                            boxShadow: `inset 3px 0 0 ${section.accent || primaryColor}, 0 2px 8px ${(section.accent || primaryColor)}22`,
                          } : {}}
                          data-testid={`store-nav-${item.label.toLowerCase().replace(/[\s&]/g, '-')}`}
                        >
                          <item.icon
                            size={18}
                            weight={active ? 'fill' : 'regular'}
                            style={active ? { color: section.accent || primaryColor } : {}}
                            className={active ? '' : 'group-hover:scale-110 transition-transform'}
                          />
                          {sidebarOpen && (
                            <span className="text-[13px] font-medium flex-1">{item.label}</span>
                          )}
                          {item.badge > 0 && sidebarOpen && (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white shadow-sm" style={{ backgroundColor: section.accent || primaryColor, boxShadow: `0 2px 8px ${(section.accent || primaryColor)}66` }}>
                              {item.badge}
                            </span>
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ))}
        </nav>

        {/* User section */}
        <div className="relative p-3 border-t border-white/10 bg-black/20 backdrop-blur-sm">
          <div className="flex items-center gap-3 px-2">
            <Link
              to={`${basePath}/account`}
              className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm overflow-hidden hover:ring-2 hover:ring-white/30 transition-all"
              style={{ backgroundColor: primaryColor }}
              data-testid="admin-account-link"
              title="Mon compte"
            >
              {user?.avatar_url ? (
                <img
                  src={user.avatar_url.startsWith('/') ? `${API_URL}${user.avatar_url}` : user.avatar_url}
                  alt={user?.name || ''}
                  className="w-full h-full object-cover"
                />
              ) : (
                user?.name?.charAt(0).toUpperCase() || 'U'
              )}
            </Link>
            {sidebarOpen && (
              <Link to={`${basePath}/account`} className="flex-1 min-w-0 hover:opacity-80 transition-opacity" data-testid="admin-account-name-link">
                <p className="text-[13px] font-medium text-white truncate">{user?.name}</p>
                <p className="text-[11px] text-white/40 truncate">{user?.role === 'admin' ? 'Administrateur' : 'Marchand'}</p>
              </Link>
            )}
            <button onClick={handleLogout} className="text-white/40 hover:text-white transition-colors" data-testid="store-admin-logout-btn">
              <SignOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      {/* ═══════ MAIN CONTENT ═══════ */}
      <div className={`flex-1 flex flex-col min-w-0 overflow-x-hidden transition-all duration-300 ml-0 ${sidebarOpen ? 'md:ml-[260px]' : 'md:ml-[72px]'}`}>
        {/* Top header bar */}
        <header className="h-14 bg-white border-b border-slate-200/60 sticky top-0 z-40 flex items-center px-3 md:px-6 gap-2 md:gap-4 min-w-0">
          <button
            onClick={() => setMobileOpen(true)}
            className="md:hidden text-slate-600 hover:text-slate-900 transition-colors p-1"
            data-testid="toggle-sidebar-mobile"
            aria-label="Ouvrir le menu"
          >
            <List size={22} />
          </button>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden md:block text-slate-400 hover:text-slate-600 transition-colors"
            data-testid="toggle-sidebar"
          >
            <List size={20} />
          </button>

          <div className="flex-1" />
          
          {/* Quick stats in header */}
          {quickStats && (
            <div className="hidden lg:flex items-center gap-5 text-sm" data-testid="admin-header-stats">
              <div className="flex items-center gap-1.5" title="Chiffre d'affaires du jour">
                <CurrencyEur size={15} className="text-emerald-500" />
                <span className="text-slate-500">CA jour :</span>
                <span className="font-bold" style={{ color: primaryColor }} data-testid="stat-ca-today">
                  {(quickStats.revenue_today || 0).toFixed(0)} €
                </span>
                {quickStats.orders_today > 0 && (
                  <span className="text-[10px] text-slate-400">({quickStats.orders_today})</span>
                )}
              </div>
              <span className="text-slate-200">|</span>
              <div className="flex items-center gap-1.5" title="Chiffre d'affaires du mois en cours">
                <ChartLine size={15} className="text-blue-500" />
                <span className="text-slate-500">CA mois :</span>
                <span className="font-bold" style={{ color: primaryColor }} data-testid="stat-ca-month">
                  {(quickStats.revenue_month || 0).toFixed(0)} €
                </span>
                {quickStats.orders_month > 0 && (
                  <span className="text-[10px] text-slate-400">({quickStats.orders_month})</span>
                )}
              </div>
              <span className="text-slate-200">|</span>
              <div className="flex items-center gap-1.5" title="Panier moyen du mois">
                <ShoppingCart size={15} className="text-amber-500" />
                <span className="text-slate-500">Panier :</span>
                <span className="font-bold" data-testid="stat-avg-basket">
                  {(quickStats.avg_basket_month || quickStats.avg_basket || 0).toFixed(0)} €
                </span>
              </div>
              <span className="text-slate-200">|</span>
              <div className="flex items-center gap-1.5" title="Nombre total de clients">
                <Users size={15} className="text-violet-500" />
                <span className="text-slate-500">Clients :</span>
                <span className="font-bold" data-testid="stat-customers">
                  {quickStats.customers_count || 0}
                </span>
              </div>
              <span className="text-slate-200">|</span>
              <div className="flex items-center gap-1.5" title="Nombre de produits en catalogue">
                <Package size={15} className="text-slate-400" />
                <span className="text-slate-500">Produits :</span>
                <span className="font-bold" data-testid="stat-products">
                  {quickStats.products_count || 0}
                </span>
              </div>
            </div>
          )}

          <div className="flex items-center gap-1.5 md:gap-2 ml-auto flex-shrink-0">
            <Button
              variant="outline" size="sm"
              className="text-xs h-8 gap-1.5"
              onClick={() => setTourOpen(true)}
              title="Guide de l'admin"
              data-testid="open-guide-tour-btn"
            >
              <Question size={14} />
              <span className="hidden sm:inline">Guide</span>
            </Button>
            <PwaInstallButton compact />
            <AdminPushToggle />
            <Button 
              variant="outline" size="sm" 
              className="text-xs h-8 gap-1.5"
              onClick={() => window.open(standalone ? '/' : `/store/${storeSlug}`, '_blank')}
            >
              <Storefront size={14} />
              <span className="hidden sm:inline">Voir le site</span>
            </Button>
          </div>
        </header>

        <AdminGuideTour open={tourOpen} onOpenChange={setTourOpen} />

        {/* Page Content */}
        <main className="flex-1 p-3 sm:p-4 md:p-6 overflow-auto min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default StoreAdminLayout;
